import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Home,
  Users,
  Wallet,
  Receipt,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  UserCircle,
  UserCog,
  Building,
  BarChart3,
  Loader2
} from 'lucide-react';
import { supabase, getCurrentProfileSafe, getCurrentBodyCorporateId } from '../lib/supabase';
import toast from 'react-hot-toast';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [brandName, setBrandName] = useState('Body Corporate');
  const [brandLogo, setBrandLogo] = useState('');
  const [brandColor, setBrandColor] = useState('#2563eb');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
    fetchBrandSettings();
  }, []);

  const fetchBrandSettings = async () => {
    const id = await getCurrentBodyCorporateId();
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('body_corporates')
        .select('name, logo_path, primary_color, style_preset')
        .eq('id', id)
        .single();

      if (!error && data) {
        setBrandName(data.name || 'Body Corporate');
        setBrandLogo(data.logo_path || '');
        setBrandColor(data.primary_color || '#2563eb');
      }
    } catch (error) {
      console.error('Error fetching brand settings:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const profile = await getCurrentProfileSafe();
      if (profile) {
        setUserName(profile.full_name || 'User');
        setUserRole(profile.role || 'staff');
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserName(user.user_metadata?.full_name || user.email?.split('@')[0] || 'User');
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
    setLoading(false);
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/flats', label: 'Flats', icon: Home },
    { path: '/owners', label: 'Owners', icon: Users },
    { path: '/accounts', label: 'Accounts', icon: Wallet },
    { path: '/transactions', label: 'Transactions', icon: Receipt },
    { path: '/statements', label: 'Statements', icon: FileText },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    { path: '/profile', label: 'Profile', icon: UserCircle },
  ];

  const adminNavItems = [
    { path: '/users', label: 'Users', icon: UserCog },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  const visibleNavItems = userRole === 'administrator'
    ? [...navItems, ...adminNavItems]
    : navItems;

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'administrator': return 'bg-purple-100 text-purple-800';
      case 'staff': return 'bg-blue-100 text-blue-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'administrator': return '👑';
      case 'staff': return '🛠️';
      case 'viewer': return '👁️';
      default: return '👤';
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {brandLogo ? (
                <img src={brandLogo} alt={brandName} className="w-10 h-10 rounded-lg object-cover border border-gray-200" />
              ) : (
                <Building className="text-primary-600" size={28} style={{ color: brandColor }} />
              )}
              <div>
                <h1 className="text-lg font-bold" style={{ color: brandColor }}>{brandName}</h1>
                <p className="text-xs text-gray-500">Levy Management</p>
              </div>
            </div>
          </div>

          {/* User Profile Section */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                {loading ? (
                  <Loader2 size={16} className="animate-spin text-primary-600" />
                ) : (
                  <span className="text-sm font-bold text-primary-600">
                    {userName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {loading ? 'Loading...' : userName}
                </p>
                {userRole && (
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full ${getRoleBadgeColor(userRole)}`}>
                    {getRoleIcon(userRole)} {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors
                  ${isActive
                    ? 'text-gray-900'
                    : 'text-gray-600 hover:bg-gray-100'
                  }
                `}
                style={({ isActive }) => isActive ? {
                  backgroundColor: `${brandColor}16`,
                  color: brandColor,
                } : undefined}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon size={20} style={{ color: brandColor }} />
                <span className="text-sm">{item.label}</span>
                {item.path === '/' && (
                  <span className="ml-auto text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${brandColor}1F`, color: brandColor }}>
                    Home
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut size={20} />
              <span className="text-sm">Logout</span>
            </button>
            <p className="text-xs text-gray-400 mt-2 text-center">
              v1.0.0
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
